import * as React from 'react';

// UTILS
import { emailRegex } from '../../utils';

// PACKAGES
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import Button from '../../components/Button';
import SearchOtherRecipients from './SearchOtherRecipients';
import GlobalModal from '../../components/GlobalModal';
import DisplayDateTime from '../../components/DisplayDateTime';
import FileUpload from '../../components/FileUpload';
import FilesList from '../../components/FilesList';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import AddIndividualModal from './Modals/AddIndividualModal';
import AddAuthorityModal from './Modals/AddAuthorityModal';

// ICONS
import { ReactComponent as EmailIcon } from '../../assets/svg/email.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/svg/envelope-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as NoteIcon } from '../../assets/svg/note-icon.svg';

// TYPES
import { IncidentFormTypes } from './IncidentSubmit';
import { IncidentEditUser, IncidentRecipientsUser } from '../../api/models';

export interface StepThreeTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	leadershipTeamData: IncidentEditUser[];
	otherRecipientsData: IncidentRecipientsUser[];
	setOtherRecipientsData: React.Dispatch<React.SetStateAction<IncidentRecipientsUser[]>>;
}

const StepThree: React.FC<StepThreeTypes> = ({
	incidentFormData,
	setIncidentFormData,

	setActiveStep,

	leadershipTeamData,

	otherRecipientsData,
	setOtherRecipientsData
}) => {
	// Error state
	const [showError, setShowError] = React.useState(false);

	// State to manage Modal
	const [showAddIndividualModal, setShowAddIndividualModal] = React.useState<boolean>(false);
	const [showAddAuthorityModal, setShowAddAuthorityModal] = React.useState<boolean>(false);

	// LOGIC FOR LEADERSHIP
	const [selectedLeadershipMemberId, setSelectedLeadershipMemberId] = React.useState<string[]>([]);

	React.useEffect(() => {
		setSelectedLeadershipMemberId(incidentFormData.leaderRecipientIds || []);

		setOtherRecipientsData(incidentFormData.otherRecipients || []);

		setSelectedEmails(incidentFormData.otherRecipientEmails || []);
	}, []);

	const selectLeadershipMemberHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = e.target;

		if (checked) {
			if (!selectedLeadershipMemberId.includes(value)) {
				setSelectedLeadershipMemberId((prev) => [...prev, value]);
			}
		} else {
			setSelectedLeadershipMemberId((prev) => prev.filter((item) => item !== value));
		}

		if (showError) {
			setShowError(false);
		}
	};

	const removeSelectLeadershipMemberHandler = (id: string | undefined) => {
		if (id !== undefined) {
			setSelectedLeadershipMemberId((prev) => prev.filter((item) => item !== id));
		}
	};

	// LOGIC FOR OTHER RECIPENTS
	const [showExistingMemberError, setShowExistingMemberError] = React.useState<boolean>(false);

	const addOtherRecipientsHandler = (item: IncidentRecipientsUser) => {
		if (item !== undefined && item.id !== undefined && !incidentFormData.otherRecipientIds?.includes(item.id)) {
			setShowExistingMemberError(false);

			setOtherRecipientsData([...otherRecipientsData, item]);

			setIncidentFormData({
				...incidentFormData,

				otherRecipientIds: [...(incidentFormData.otherRecipientIds || []), item.id || '']
			});
		} else if (item !== undefined && item.id !== undefined && incidentFormData.otherRecipientIds?.includes(item.id)) {
			setShowExistingMemberError(true);
		}
	};

	const removeOtherRecipientsHandler = (id: string | undefined) => {
		if (id !== undefined) {
			const newList = otherRecipientsData.filter((item) => item.id !== id);

			setOtherRecipientsData(newList);

			const incidentIds =
				incidentFormData.otherRecipientIds && incidentFormData.otherRecipientIds.filter((item) => item !== id);

			setIncidentFormData({
				...incidentFormData,

				otherRecipientIds: [...(incidentIds || [])]
			});
		}
	};

	React.useEffect(() => {
		setOtherRecipientsData(otherRecipientsData);
	}, [otherRecipientsData]);

	// LOGIC FOR EMAIL INPUT
	const [selectedEmails, setSelectedEmails] = React.useState<string[]>(incidentFormData.otherRecipientEmails || []);

	const [emailInput, setEmailInput] = React.useState<string>('');

	const [emailInputError, setEmailInputError] = React.useState<boolean>(false);

	const [isAddEmailFieldActive, setIsAddEmailFieldActive] = React.useState(false);

	React.useEffect(() => {
		setSelectedEmails(selectedEmails);
	}, [selectedEmails]);

	const emailInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newEmailInput = e.target.value;

		setEmailInput(newEmailInput);

		if (emailInputError && newEmailInput.match(emailRegex)) {
			setEmailInputError(false);
		}
	};

	const addEmailHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (emailInput.match(emailRegex)) {
			setEmailInputError(false);

			setSelectedEmails((prevState) => [...prevState, emailInput]);

			setEmailInput('');

			setIncidentFormData({
				...incidentFormData,

				otherRecipientEmails: [...(incidentFormData.otherRecipientEmails || []), emailInput]
			});
		} else {
			setEmailInputError(true);
		}
	};

	const removeEmailHandler = (index: number) => {
		const newSelectedEmail = selectedEmails.filter((item, itemIndex) => itemIndex !== index);
		setSelectedEmails(newSelectedEmail);

		setIncidentFormData({
			...incidentFormData,
			otherRecipientEmails: [...(newSelectedEmail || [])]
		});
	};

	// FORM SET UP
	const initialValuesStepThree = {
		individualsNotified: incidentFormData.individualsNotified || false,
		individuals: incidentFormData.individuals || [],
		authorityNotified: incidentFormData.authorityNotified || false,
		authorities: incidentFormData.authorities || [],
		notes: incidentFormData.notes || '',
		newAttachments: incidentFormData.newAttachments || [],
		createAttachments: incidentFormData.createAttachments || [],
		attachments: incidentFormData.attachments || [],
		deleteAttachmentIds: incidentFormData.deleteAttachmentIds || []
	};

	const validationSchemaStepThree = Yup.object({
		individuals: Yup.array().when('individualsNotified', {
			is: true,
			then: Yup.array().min(1, 'Required')
		}),
		authorities: Yup.array().when('authorityNotified', {
			is: true,
			then: Yup.array().min(1, 'Required')
		})
	});

	// SUBMIT LOGIC
	const onSubmitStepThree = (values: any) => {
		if (selectedLeadershipMemberId.length < 1) {
			setShowError(true);
		} else {
			setShowError(false);

			setIncidentFormData({
				...incidentFormData,

				leaderRecipientIds: [...selectedLeadershipMemberId],

				individualsNotified: values.individualsNotified,
				individuals: values.individuals,
				authorityNotified: values.authorityNotified,
				authorities: values.authorities,
				notes: values.notes,

				newAttachments: values.newAttachments,
				createAttachments: values.createAttachments,
				attachments: values.attachments,
				deleteAttachmentIds: values.deleteAttachmentIds
			});

			setActiveStep('step4');

			window.scrollTo(0, 0);
		}
	};

	return (
		<Formik
			initialValues={initialValuesStepThree}
			validationSchema={validationSchemaStepThree}
			onSubmit={onSubmitStepThree}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main w-100">
								{/* INDIVIDUAL NOTIFIED */}
								<h3>Individuals notified</h3>

								<p className="sub-grey-text mtop-10">
									Please record the details of all people who were notified, or all attempted notifications, for this
									incident, injury, trauma or illness.
								</p>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group mbtom-40">
									<label className="label">Were there any attempts to notify individuals about this incident?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="individualsNotified"
												id="individualsNotified"
												type="radio"
												value={true}
												checked={formik.values.individualsNotified === true}
												onChange={() => formik.setFieldValue('individualsNotified', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="individualsNotified"
												id="individualsNotified"
												type="radio"
												value={false}
												checked={formik.values.individualsNotified === false}
												onChange={() => formik.setFieldValue('individualsNotified', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="individualsNotified" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.individualsNotified === true && (
									<div className="form-group w-60 mbtom-40">
										<label>
											List all individuals who were notified, or all attempted notifications about this incident
										</label>

										<div className="general-divider mbtom-0"></div>

										<FieldArray name="individuals">
											{(props) => {
												const { push, remove, form } = props;
												const { values } = form;
												const { individuals } = values;

												return (
													<>
														{individuals?.length > 0 &&
															individuals.map((item: any, index: number) => {
																return (
																	<div className="incident-detail-row row" key={index}>
																		<div className="col-12 pdl-0">
																			<p className="name">{item.name}</p>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>{item.role}</p>
																		</div>

																		<div className="col-5">
																			<p>{item.contactMethod}</p>
																		</div>

																		<div className="col-3 text-right pdr-0">
																			<Button
																				onClick={() => {
																					remove(index);
																				}}
																				className="btn-text pd-0"
																			>
																				Remove
																			</Button>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>
																				<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																			</p>
																		</div>

																		<div className="col-5">
																			<p>Notification {item.isNotifiedSuccess ? 'successful' : 'unsuccessful'}</p>
																		</div>
																	</div>
																);
															})}

														<Button
															onClick={() => {
																setIncidentFormData((prevState) => ({
																	...prevState,

																	leaderRecipientIds: [...selectedLeadershipMemberId],
																	individualsNotified: values.individualsNotified,
																	individuals: values.individuals,
																	authorityNotified: values.authorityNotified,
																	authorities: values.authorities,
																	notes: values.notes,
																	newAttachments: values.newAttachments,
																	createAttachments: values.createAttachments,
																	attachments: values.attachments,
																	deleteAttachmentIds: values.deleteAttachmentIds
																}));

																setShowAddIndividualModal(true);
															}}
															type="button"
															className="add-witness mbtom-0"
														>
															<PlusIcon className="icon" />
															Add individual
														</Button>
													</>
												);
											}}
										</FieldArray>

										<ErrorMessage name="individuals">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								)}

								{/* AUTHORITY NOTIFIED */}
								<h3>Authorities notified</h3>

								<p className="sub-grey-text mtop-10">
									There are some instances where an authority must be notified (E.g. mandatory reporting, reportable
									conduct or serious incidents where government authorities or regulators must be notified).
								</p>

								<p className="sub-grey-text">
									Please record the details of all authorities that were notified, or all attempted notifications, for
									this incident, injury, trauma or illness.
								</p>

								<div className="general-divider mbtom-30"></div>

								<div className="form-group mbtom-40">
									<label className="label">Were there any attempts to notify authorities about this incident?</label>

									<div className="form-field radio-group">
										<div className="radio-row mbtom-0">
											<Field
												name="authorityNotified"
												id="authorityNotified"
												type="radio"
												value={true}
												checked={formik.values.authorityNotified === true}
												onChange={() => formik.setFieldValue('authorityNotified', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row mbtom-0">
											<Field
												name="authorityNotified"
												id="authorityNotified"
												type="radio"
												value={false}
												checked={formik.values.authorityNotified === false}
												onChange={() => formik.setFieldValue('authorityNotified', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="authorityNotified" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.authorityNotified === true && (
									<div className="form-group w-60 mbtom-40">
										<label>
											List all authorities who were notified, or all attempted notifications about this incident
										</label>

										<div className="general-divider mbtom-0"></div>

										<FieldArray name="authorities">
											{(props) => {
												const { push, remove, form } = props;
												const { values } = form;
												const { authorities } = values;

												return (
													<>
														{authorities?.length > 0 &&
															authorities.map((item: any, index: number) => {
																return (
																	<div className="incident-detail-row row" key={index}>
																		<div className="col-4 pdl-0">
																			<p className="name">{item.authorityName}</p>
																		</div>

																		<div className="col-5">
																			<p>{item.reason}</p>
																		</div>

																		<div className="col-3"></div>

																		<div className="col-4 pdl-0">
																			<p>{item.notifiedName}</p>
																		</div>

																		<div className="col-5">
																			<p>{item.contactMethod}</p>
																		</div>

																		<div className="col-3 text-right pdr-0">
																			<Button
																				onClick={() => {
																					remove(index);
																				}}
																				className="btn-text pd-0"
																			>
																				Remove
																			</Button>
																		</div>

																		<div className="col-4 pdl-0">
																			<p>
																				<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																			</p>
																		</div>

																		<div className="col-5">
																			<p>Notification {item.isNotifiedSuccess ? 'successful' : 'unsuccessful'}</p>
																		</div>
																	</div>
																);
															})}

														<Button
															onClick={() => {
																setIncidentFormData((prevState) => ({
																	...prevState,

																	leaderRecipientIds: [...selectedLeadershipMemberId],
																	individualsNotified: values.individualsNotified,
																	individuals: values.individuals,
																	authorityNotified: values.authorityNotified,
																	authorities: values.authorities,
																	notes: values.notes,
																	newAttachments: values.newAttachments,
																	createAttachments: values.createAttachments,
																	attachments: values.attachments,
																	deleteAttachmentIds: values.deleteAttachmentIds
																}));

																setShowAddAuthorityModal(true);
															}}
															type="button"
															className="add-witness mbtom-0"
														>
															<PlusIcon className="icon" />
															Add an authority
														</Button>
													</>
												);
											}}
										</FieldArray>

										<ErrorMessage name="authorities">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								)}

								{/* INCIDENT REPORT RECIPIENTS */}

								<h3>Incident report recipients</h3>

								<p className="sub-grey-text mtop-10">
									Select recipients who should receive a copy of this report. At least 1 leader must be selected.
								</p>

								<div className="general-divider mbtom-30"></div>

								{/* LEADERS NOTIFIED */}

								<div className="form-group w-60">
									<div className="general-notification-box">
										<NoteIcon />

										<div className="info-text w-100">
											<p>
												<strong>Under no circumstances</strong> should an alleged perpetrator receive an incident
												report.
											</p>
										</div>
									</div>

									<h6>Leaders</h6>

									<p className="sub-grey-text small mtop-10">
										Please select which leaders should receive this incident report. Leaders of organisations should
										generally receive copies of incident reports so that they are aware of safety issues unless it would
										cause harm to a victim by notifying them.
									</p>

									<div className="form-field">
										{leadershipTeamData &&
											leadershipTeamData.length > 0 &&
											leadershipTeamData.map((item, index) => {
												return (
													<div key={index} className="checkbox-row">
														<input
															type="checkbox"
															name="leaderRecipientIdsInput"
															value={item.id}
															onChange={selectLeadershipMemberHandler}
															checked={selectedLeadershipMemberId.includes(item.id || '')}
														/>
														<label>{item.name}</label>
													</div>
												);
											})}
									</div>
								</div>
							</div>
						</div>

						{/* PARENTS, GUARDIANS AND CARERS */}
						<h6>Parents, guardians and staff</h6>

						<div className="form-group w-60 mbtom-0">
							<p className="sub-grey-text small mtop-10">
								Please select (or enter an email address for) the parents or guardians who should receive this incident
								report.
							</p>

							<p className="sub-grey-text small">
								Please add any other staff who need to receive this incident report, balancing the need to protect the
								victim's privacy with the need to alert relevant staff for the child's safety.
							</p>
						</div>

						<section className="people-search-wrapper mtop-0">
							<SearchOtherRecipients
								placeholder="Search parents, guardians or teacher names..."
								onChange={(item: IncidentRecipientsUser) => addOtherRecipientsHandler(item)}
								setShowExistingMemberError={setShowExistingMemberError}
							/>

							{showExistingMemberError && <div className="error-message">This member has already been added</div>}
						</section>

						<div className="or-divider w-50">
							<div className="text-wrapper">or</div>
						</div>

						<section className="add-email-wrapper">
							<h2 className="add-email-heading">Enter email address if contact isn't listed above.</h2>

							<div className="add-email-content" onClick={() => setIsAddEmailFieldActive(!isAddEmailFieldActive)}>
								<div className={`add-email-container ${isAddEmailFieldActive ? 'active' : ''}`}>
									<EmailIcon className="email-icon" />

									<input
										className="email-input"
										type="text"
										placeholder="Enter an email..."
										onChange={emailInputHandler}
										value={emailInput}
									/>
								</div>

								<Button className="btn-secondary" onClick={addEmailHandler}>
									Add
								</Button>
							</div>

							{emailInputError && <div className="error-message">Invalid email format</div>}
						</section>

						{/* LIST OF RECIPIENTS */}
						<h3 className="mtop-30 mbtom-20">List of recipients</h3>

						<div className="general-divider w-60 mbtom-0"></div>

						{selectedLeadershipMemberId?.length === 0 &&
						otherRecipientsData?.length === 0 &&
						selectedEmails?.length === 0 ? (
							<p className="mtop-20">No contacts added</p>
						) : null}

						<section className="info-section incident-form mtop-0">
							{leadershipTeamData && leadershipTeamData.length > 0 && (
								<ul>
									{leadershipTeamData.map((item) => {
										if (item.id && selectedLeadershipMemberId.includes(item.id)) {
											return (
												<li key={item.id} className="row-container">
													<div className="row detail-row">
														<div className="col-2 pdl-0">
															<CircleImage firstName={item.name} lastName={item.name} src={item.profileImage} />
														</div>

														<div className="col-8 pdl-0">
															<div className="row detail-info center">
																<div className="col-12 detail-name">
																	<p className="detail-name mbtom-0">{item.name}</p>

																	<p className="label grey mbtom-0">{item.role}</p>
																</div>
															</div>
														</div>

														<div className="col-2 link-group pdr-0">
															<Button
																className="btn-text pdr-0 mtop-0"
																onClick={() => removeSelectLeadershipMemberHandler(item.id)}
															>
																Remove
															</Button>
														</div>
													</div>
												</li>
											);
										} else return null;
									})}
								</ul>
							)}

							<ul>
								{otherRecipientsData &&
									otherRecipientsData.length > 0 &&
									otherRecipientsData.map((item, index) => {
										return (
											<li key={index} className="row-container">
												<div className="row detail-row">
													<div className="col-2 pdl-0">
														<CircleImage firstName={item.name} lastName={item.name} src={item.profileImage} />
													</div>

													<div className="col-8 pdl-0">
														<div className="row detail-info center">
															<div className="col-12 detail-name">
																<p className="detail-name mbtom-0">{item.name}</p>

																{item.children && item.children.length > 0 ? (
																	<p className="label grey mbtom-0">
																		<strong>Parent</strong> to {item.children.join(', ')}
																	</p>
																) : (
																	<p className="label grey mbtom-0">{item.role}</p>
																)}
															</div>
														</div>
													</div>

													<div className="col-2 link-group pdr-0">
														<Button
															className="btn-text pdr-0 mtop-0"
															onClick={() => removeOtherRecipientsHandler(item.id)}
														>
															Remove
														</Button>
													</div>
												</div>
											</li>
										);
									})}
							</ul>

							<ul>
								{selectedEmails &&
									selectedEmails.length > 0 &&
									selectedEmails.map((item, index) => {
										return (
											<li key={index} className="row-container">
												<div className="row detail-row">
													<div className="col-2 pdl-0">
														<div className="incident-icon-container">
															<EnvelopeIcon />
														</div>
													</div>

													<div className="col-8 pdl-0">
														<div className="row detail-info center">
															<div className="col-12 detail-name ellipsis">{item}</div>
														</div>
													</div>

													<div className="col-2 link-group pdr-0">
														<Button className="btn-text pdr-0 mtop-0" onClick={() => removeEmailHandler(index)}>
															Remove
														</Button>
													</div>
												</div>
											</li>
										);
									})}
							</ul>
						</section>

						{/* ADDITIONAL INFORMATION */}

						<h3 className="mtop-30">Additional information</h3>

						<div className="general-divider mbtom-30 mtop-20"></div>

						<div className="form-group w-60 mbtom-40">
							<label htmlFor="notes" className="label">
								Notes
							</label>

							<p className="sub-grey-text small mtop-10">Are there any other notes relating to this incident?</p>

							<div className="form-field">
								<Field as="textarea" name="notes" id="notes" type="text" className="form-input" />

								<ErrorMessage name="notes" component="div" className="error-message" />
							</div>
						</div>

						<div className="form-group w-60">
							<label className="label">Documents</label>

							<p className="sub-grey-text small mtop-10">Add any supporting documents or images.</p>

							<FilesList
								files={
									(incidentFormData.attachments &&
										incidentFormData.attachments.filter(
											(item) => !incidentFormData.deleteAttachmentIds?.includes(item.id || '')
										)) ||
									[]
								}
								onChange={(vals: string[]) => formik.setFieldValue('deleteAttachmentIds', vals)}
								multiple={true}
							/>

							<FileUpload
								name="createAttachments"
								onChange={(vals: string[], files: any[]) => {
									formik.setFieldValue('createAttachments', vals);
									formik.setFieldValue('newAttachments', files);
								}}
								files={incidentFormData.newAttachments}
								multiple={true}
								maxSizeMb={5}
							></FileUpload>

							<p className="sub-grey-text mtop-30">
								<strong>You may </strong>
								upload documents that provide further details about the incident, injury, trauma or illness. For
								example, you may attach a more detailed description of the incident, a photograph of the location where
								the incident occurred, or a diagram of the part of the body where the child was injured.
							</p>

							<div className="general-notification-box">
								<NoteIcon />

								<div className="info-text w-100">
									<p>
										<strong>You must not </strong>upload any images that may identify a child, or any intimate images of
										a child’s body parts e.g. you must not upload images of nappy rash or injuries to the bottom or
										genitals of a child.
									</p>
								</div>
							</div>
						</div>

						{/* SUBMIT BUTTON AND GENERAL ERROR MESSAGE */}
						<ScrollToTopButton />

						<div className="general-divider"></div>

						{showError && (
							<div className="error-message mtop-20">You need to add at least 1 leadership Team member</div>
						)}

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>

						<GlobalModal
							isOpen={showAddIndividualModal}
							setIsOpen={setShowAddIndividualModal}
							titleText="Add an individual"
						>
							<AddIndividualModal incidentFormData={incidentFormData} setIncidentFormData={setIncidentFormData} />
						</GlobalModal>

						<GlobalModal
							isOpen={showAddAuthorityModal}
							setIsOpen={setShowAddAuthorityModal}
							titleText="Add an authority"
						>
							<AddAuthorityModal incidentFormData={incidentFormData} setIncidentFormData={setIncidentFormData} />
						</GlobalModal>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepThree;
